import React from 'react';
import { Award } from 'lucide-react';

const ArrowButton = ({ dir, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={dir === 'prev' ? 'Imagen anterior' : 'Imagen siguiente'}
    data-testid={`modal-carousel-${dir}`}
    className={`absolute ${dir === 'prev' ? 'left-2' : 'right-2'} top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-white/85 hover:bg-white shadow-md flex items-center justify-center transition-all backdrop-blur-sm`}
  >
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 sm:w-5 sm:h-5 text-slate-800">
      {dir === 'prev' ? <polyline points="15 18 9 12 15 6"/> : <polyline points="9 18 15 12 9 6"/>}
    </svg>
  </button>
);

export const ProductImageCarousel = ({
  product, galleryImages, galleryIdx, heroImage,
  isTextureView, isSizeImgView, selectedCottonType, selectedSize,
  showingTexture, carouselOverride,
  onPrev, onNext, onSelectThumb, onTouchStart, onTouchEnd,
}) => {
  const carouselLen = galleryImages.length;

  const resolveImgSrc = () => {
    const sizeImg = !carouselOverride && product.sizeImages && product.sizeImages[selectedSize];
    if (sizeImg) return sizeImg;
    if (product.hasCottonType && showingTexture && !carouselOverride) {
      return selectedCottonType === 'cotton_edge' ? '/products/cotton-edge.webp' : '/products/cotton-smooth.webp';
    }
    return heroImage;
  };

  const showThumbs = product.hasCottonType || galleryImages.length > 1;

  return (
    <div className="bg-slate-50 p-2 sm:p-4 md:p-6 flex flex-col">
      <div
        className="relative aspect-[3/4] w-full max-w-md mx-auto rounded-2xl overflow-hidden bg-white select-none"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        data-testid="product-image-carousel"
      >
        <img
          src={resolveImgSrc()}
          alt={product.name}
          className="absolute inset-0 w-full h-full object-cover object-center pointer-events-none"
          draggable={false}
        />
        {/* Carousel arrows — visible whenever there are 2+ gallery images.
            Clicking exits texture/size-image view automatically. */}
        {carouselLen > 1 && (
          <>
            <ArrowButton dir="prev" onClick={onPrev} />
            <ArrowButton dir="next" onClick={onNext} />
            {!isTextureView && !isSizeImgView && (
              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 flex gap-1.5 px-2 py-1 rounded-full bg-black/30 backdrop-blur-sm">
                {galleryImages.map((img, i) => (
                  <span
                    key={img}
                    className={`block rounded-full transition-all ${i === galleryIdx ? 'bg-white w-4 h-1.5' : 'bg-white/55 w-1.5 h-1.5'}`}
                  />
                ))}
              </div>
            )}
          </>
        )}
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-green-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full">
          <Award className="h-3 w-3" />
          {product.kashrut?.split(' - ')[0] || 'Kosher'}
        </div>
      </div>
      {showThumbs && (
        <div className="flex gap-2 mt-2 sm:mt-3 overflow-x-auto pb-1">
          {galleryImages.map((img, idx) => {
            const isActive = galleryIdx === idx && (!product.hasCottonType || !showingTexture);
            return (
              <button
                key={img + idx}
                onClick={() => onSelectThumb(idx)}
                className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-xl border-2 overflow-hidden transition-all ${isActive ? 'border-cyan-500 ring-2 ring-cyan-200' : 'border-slate-200 hover:border-slate-300'}`}
                data-testid={`gallery-thumb-${idx}`}
              >
                <img src={img} alt={`${product.name} ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};
